import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Calendar } from "primereact/calendar";
import { Sidebar } from "primereact/sidebar";
import { Divider } from "primereact/divider";
import { Dialog } from "primereact/dialog";

import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import GstSummaryCards from "./GstSummaryCards";
import { getAllDetails, getSpecificDetails, postAllData, updateData } from "../../service/ApiService";
import {
  GET_UBER_CALCULATION,
  ADD_UBER_CALCULATION,
  UPDATE_UBER_CALCULATION,
  PREVIEW_UBER_CALCULATION,
  LIST_GST_INCOME,
  LIST_GST_EXPENSE,
  ADD_GST_INCOME,
  ADD_GST_EXPENSE,
} from "../../config/ApiConstant";
import { APP_BASE_URL } from "../../appconfig/Settings";
import axiosInstance from "../../config/axiosInstance";

// ─── Constants ───────────────────────────────────────────────────────────────

const defaultIncomeRow = () => ({
  source_type: "",
  gross: null,
  tips: null,
  service_fees: null,
  has_gst: false,
  is_uber_main: false,
});

const defaultExpenseRow = () => ({
  description: "",
  gross: null,
});

const fmt = (n: any) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const calcIncomeRow = (row: any, gst_percent: number) => {
  const gross = parseFloat(row.gross) || 0;
  const tips = parseFloat(row.tips) || 0;
  const service_fees = parseFloat(row.service_fees) || 0;
  const has_gst = row.has_gst === true || row.has_gst === 1 || row.has_gst === "1";
  const is_uber_main = row.is_uber_main === true || row.is_uber_main === 1 || row.is_uber_main === "1";
  const gst_rate = gst_percent / 100;
  const net_after_fees = is_uber_main ? gross - service_fees : gross - service_fees + tips;
  const gst = has_gst ? (net_after_fees - tips) / (1 + gst_rate) * gst_rate : 0;
  return { net_after_fees, gst };
};

const calcExpenseRow = (row: any, gst_percent: number) => {
  const gross = parseFloat(row.gross) || 0;
  const gst_rate = gst_percent / 100;
  const gst = gross / (1 + gst_rate) * gst_rate;
  const net = gross - gst;
  return { gst, net };
};

const round2 = (n: number) => Math.round(n * 100) / 100;

const computeUberTotals = (income_data: any[], expense_data: any[], gst_percent: number, car_gst: number, insurance_amount: number) => {
  const gst_rate = (gst_percent ?? 5) / 100;

  let total_gross_income = 0, total_tips = 0, total_net_income = 0;
  let total_service_fees = 0, total_net_after_fees = 0;
  let total_gst_collected = 0, total_taxable_income = 0;
  let non_taxable_sum = 0, sum_net_after_fees_gst = 0;

  (income_data || []).forEach((row: any) => {
    const gross = parseFloat(row.gross) || 0;
    const tips = parseFloat(row.tips) || 0;
    const service_fees = parseFloat(row.service_fees) || 0;
    const has_gst = row.has_gst === true || row.has_gst === 1 || row.has_gst === "1";
    const is_uber_main = row.is_uber_main === true || row.is_uber_main === 1 || row.is_uber_main === "1";

    const net_income = gross - tips;
    const net_after_fees = is_uber_main ? gross - service_fees : gross - service_fees + tips;

    let gst = 0, taxable_income = 0;
    if (has_gst) {
      gst = (net_after_fees - tips) / (1 + gst_rate) * gst_rate;
      taxable_income = net_after_fees - gst - tips;
      sum_net_after_fees_gst += net_after_fees;
    } else {
      non_taxable_sum += gross;
    }

    total_gross_income += gross;
    total_tips += tips;
    total_net_income += net_income;
    total_service_fees += service_fees;
    total_net_after_fees += net_after_fees;
    total_gst_collected += gst;
    total_taxable_income += taxable_income;
  });

  const non_taxable_line_102 = total_tips + non_taxable_sum;

  let total_expense_gross = 0, total_expense_gst = 0, total_expense_net = 0;
  (expense_data || []).forEach((row: any) => {
    const gross = parseFloat(row.gross) || 0;
    const gst = gross / (1 + gst_rate) * gst_rate;
    const net = gross - gst;
    total_expense_gross += gross;
    total_expense_gst += gst;
    total_expense_net += net;
  });

  const insurance_amount_val = parseFloat(String(insurance_amount)) || 0;
  total_expense_net += insurance_amount_val;

  const taxable_income_line_101 = round2(total_taxable_income);
  const gst_collected = round2(total_gst_collected);
  const car_gst_val = parseFloat(String(car_gst)) || 0;
  const gst_itc = round2(total_expense_gst + car_gst_val);
  const balance_due = round2(gst_collected - gst_itc);
  const gst_simple = round2(total_taxable_income * 3.6 / 100);
  const simplified_taxable_income_101 = round2(sum_net_after_fees_gst - gst_simple);

  return {
    taxable_income_line_101,
    non_taxable_line_102: round2(non_taxable_line_102),
    total_net_after_fees: round2(total_net_after_fees),
    total_gross_income: round2(total_gross_income),
    total_tips: round2(total_tips),
    total_service_fees: round2(total_service_fees),
    total_gst_collected: round2(total_gst_collected),
    total_taxable_income: round2(total_taxable_income),
    total_expense_gross: round2(total_expense_gross),
    total_expense_gst: round2(total_expense_gst),
    total_expense_net: round2(total_expense_net),
    gst_collected,
    gst_itc,
    balance_due,
    gst_simple,
    simplified_taxable_income_101,
  };
};

// ─── Component ───────────────────────────────────────────────────────────────

const AddEditUberCalc = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const decodedId = id ? atob(id) : null;

  const navigate = useNavigate();
  const location = useLocation();
  const prefill = !isEdit ? (location.state as any) : null;
  const toast = useRef<any>(null);

  const [formData, setFormData] = useState<any>({
    name: prefill?.name || "",
    sin_number: prefill?.sin_number || "",
    gst_no: "",
    access_code: "",
    mobile_no: prefill?.mobile_no || "",
    year: String(new Date().getFullYear() - 1),
    gst_percent: 5,
    car_gst: 0,
    insurance_amount: null,
    tax_form_id: prefill?.tax_form_id || null,
  });
  const [incomeRows, setIncomeRows] = useState<any[]>([defaultIncomeRow()]);
  const [expenseRows, setExpenseRows] = useState<any[]>([defaultExpenseRow()]);
  const [errors, setErrors] = useState<any>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [pageLoad, setPageLoad] = useState(!isEdit);

  const [incomeTypeOptions, setIncomeTypeOptions] = useState<any[]>([]);
  const [expenseDescOptions, setExpenseDescOptions] = useState<any[]>([]);

  // add-type dialog state
  const [incomeDialogVisible, setIncomeDialogVisible] = useState(false);
  const [newIncomeForm, setNewIncomeForm] = useState({ name: "", has_gst: false, is_uber_main: false });
  const [incomeAddLoading, setIncomeAddLoading] = useState(false);
  const [incomeNameError, setIncomeNameError] = useState("");

  const [expenseDialogVisible, setExpenseDialogVisible] = useState(false);
  const [newExpenseName, setNewExpenseName] = useState("");
  const [expenseAddLoading, setExpenseAddLoading] = useState(false);
  const [expenseNameError, setExpenseNameError] = useState("");

  // preview state
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitFromPreviewLoading, setSubmitFromPreviewLoading] = useState(false);

  const liveCalc = useMemo(
    () => computeUberTotals(incomeRows, expenseRows, formData.gst_percent ?? 5, formData.car_gst ?? 0, formData.insurance_amount ?? 0),
    [incomeRows, expenseRows, formData.gst_percent, formData.car_gst, formData.insurance_amount]
  );

  document.title = (isEdit ? "Edit" : "Add") + " GST Calc | Nagra Accounting";

  useEffect(() => {
    fetchDropdownOptions();
    if (isEdit) getDetail();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchDropdownOptions = async () => {
    try {
      const [incomeRes, expenseRes] = await Promise.all([
        getAllDetails(LIST_GST_INCOME),
        getAllDetails(LIST_GST_EXPENSE),
      ]);
      const incomeOpts = Array.isArray(incomeRes)
        ? incomeRes.map((item: any) => ({
          label: item.name ?? item.label,
          value: item.name ?? item.value,
          has_gst: item.has_gst ?? false,
          is_uber_main: item.is_uber_main ?? false,
        }))
        : [];
      const expenseOpts = Array.isArray(expenseRes)
        ? expenseRes.map((item: any) => ({
          label: item.name ?? item.label,
          value: item.name ?? item.value,
        }))
        : [];
      setIncomeTypeOptions(incomeOpts);
      setExpenseDescOptions(expenseOpts);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to load dropdown options" });
    }
  };

  const handleAddIncomeType = async () => {
    if (!newIncomeForm.name.trim()) { setIncomeNameError("Name is required *"); return; }
    setIncomeNameError("");
    setIncomeAddLoading(true);
    try {
      await postAllData(ADD_GST_INCOME, newIncomeForm);
      toast.current?.show({ severity: "success", summary: "Success", detail: "Income type added" });
      setIncomeDialogVisible(false);
      setNewIncomeForm({ name: "", has_gst: false, is_uber_main: false });
      setIncomeNameError("");
      fetchDropdownOptions();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Error", detail: error?.response?.data?.message || "Failed to add income type" });
    } finally {
      setIncomeAddLoading(false);
    }
  };

  const handleAddExpenseDesc = async () => {
    if (!newExpenseName.trim()) { setExpenseNameError("Name is required *"); return; }
    setExpenseNameError("");
    setExpenseAddLoading(true);
    try {
      await postAllData(ADD_GST_EXPENSE, { name: newExpenseName });
      toast.current?.show({ severity: "success", summary: "Success", detail: "Expense type added" });
      setExpenseDialogVisible(false);
      setNewExpenseName("");
      setExpenseNameError("");
      fetchDropdownOptions();
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: "Error", detail: error?.response?.data?.message || "Failed to add expense type" });
    } finally {
      setExpenseAddLoading(false);
    }
  };

  const getDetail = async () => {
    try {
      const res = await getSpecificDetails(GET_UBER_CALCULATION, decodedId);
      setFormData({
        name: res.name || "",
        sin_number: res.sin_number || "",
        gst_no: res.gst_no || "",
        access_code: res.access_code || "",
        mobile_no: res.mobile_no || "",
        year: res.year || "",
        gst_percent: res.gst_percent ?? 5,
        car_gst: res.car_gst ?? 0,
        insurance_amount: res.insurance_amount ?? null,
        tax_form_id: res.tax_form_id || null,
      });
      let incomeData = res.income_data;
      let expenseData = res.expense_data;
      if (typeof incomeData === "string") { try { incomeData = JSON.parse(incomeData); } catch { incomeData = []; } }
      if (typeof expenseData === "string") { try { expenseData = JSON.parse(expenseData); } catch { expenseData = []; } }
      setIncomeRows(Array.isArray(incomeData) && incomeData.length ? incomeData : [defaultIncomeRow()]);
      setExpenseRows(Array.isArray(expenseData) && expenseData.length ? expenseData : [defaultExpenseRow()]);
      setPageLoad(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Failed to load record",
      });
      setPageLoad(true);
    }
  };

  // ─── Basic field change ──────────────────────────────────────────────────

  const onFieldChange = (name: string, val: any) => {
    setFormData((prev: any) => ({ ...prev, [name]: val }));
    setErrors((prev: any) => ({ ...prev, [name]: "" }));
  };

  // ─── Income row handlers ─────────────────────────────────────────────────

  const onIncomeChange = (index: number, name: string, val: any) => {
    setIncomeRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [name]: val };
      if (name === "source_type") {
        const match = incomeTypeOptions.find((o) => o.value === val);
        if (match) {
          rows[index].has_gst = match.has_gst;
          rows[index].is_uber_main = match.is_uber_main;
        }
      }
      return rows;
    });
    setErrors((prev: any) => ({ ...prev, [`income_${index}_${name}`]: "" }));
  };

  const addIncomeRow = () => setIncomeRows((prev) => [...prev, defaultIncomeRow()]);
  const removeIncomeRow = (index: number) => setIncomeRows((prev) => prev.filter((_, i) => i !== index));

  const handleFormKeyNav = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const inputs = Array.from(
      document.querySelectorAll<HTMLElement>('input:not([disabled]):not([readonly])')
    ).filter((el) => el.offsetParent !== null);
    const idx = inputs.indexOf(document.activeElement as HTMLElement);
    if (idx === -1) return;
    e.preventDefault();
    if (e.key === "ArrowRight" && idx < inputs.length - 1) inputs[idx + 1].focus();
    if (e.key === "ArrowLeft" && idx > 0) inputs[idx - 1].focus();
  };

  // ─── Expense row handlers ────────────────────────────────────────────────

  const onExpenseChange = (index: number, name: string, val: any) => {
    setExpenseRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], [name]: val };
      return rows;
    });
    setErrors((prev: any) => ({ ...prev, [`expense_${index}_${name}`]: "" }));
  };

  const addExpenseRow = () => setExpenseRows((prev) => [...prev, defaultExpenseRow()]);
  const removeExpenseRow = (index: number) => setExpenseRows((prev) => prev.filter((_, i) => i !== index));

  // ─── Validation ──────────────────────────────────────────────────────────

  const validate = () => {
    const errs: any = {};
    let isError = false;

    if (!formData.name?.trim()) { errs.name = "Name is required *"; isError = true; }
    if (!formData.mobile_no?.trim()) { errs.mobile_no = "Mobile No is required *"; isError = true; }
    if (!formData.sin_number?.trim()) {
      errs.sin_number = "SIN Number is required *"; isError = true;
    }
    if (formData.gst_no?.trim() && !/^\d{9}$/.test(formData.gst_no.trim())) {
      errs.gst_no = "GST No must be exactly 9 digits *"; isError = true;
    }
    if (formData.access_code?.trim() && !/^\d{4}$/.test(formData.access_code.trim())) {
      errs.access_code = "Access Code must be exactly 4 digits *"; isError = true;
    }
    if (!formData.year) { errs.year = "Year is required *"; isError = true; }

    incomeRows.forEach((row, i) => {
      if (!row.source_type) { errs[`income_${i}_source_type`] = "Required *"; isError = true; }
      if (row.gross === null || row.gross === undefined || row.gross === "") {
        errs[`income_${i}_gross`] = "Required *"; isError = true;
      }
    });


    setErrors(errs);
    return isError;
  };

  const buildPayload = () => ({
    name: formData.name,
    sin_number: formData.sin_number,
    gst_no: formData.gst_no,
    access_code: formData.access_code,
    mobile_no: formData.mobile_no,
    year: formData.year,
    gst_percent: formData.gst_percent ?? 5,
    car_gst: formData.car_gst ?? 0,
    insurance_amount: formData.insurance_amount ?? null,
    tax_form_id: formData.tax_form_id ?? null,
    income_data: incomeRows,
    expense_data: expenseRows.filter((row) => row.description !== "" && row.description != null),
  });

  // ─── Preview ──────────────────────────────────────────────────────────────

  const handlePreview = async () => {
    if (validate()) return;
    setPreviewLoading(true);
    try {
      const res = await axiosInstance.post(APP_BASE_URL + PREVIEW_UBER_CALCULATION, buildPayload());
      setPreviewData(res?.data?.data ?? res?.data);
      setPreviewVisible(true);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Preview failed",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // ─── Submit (from form or preview sidebar) ────────────────────────────────

  const submitRecord = async (setLoading: (v: boolean) => void) => {
    if (validate()) return;
    setLoading(true);
    try {
      const response = isEdit
        ? await updateData(UPDATE_UBER_CALCULATION, decodedId, buildPayload())
        : await postAllData(ADD_UBER_CALCULATION, buildPayload());

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: response?.message || (isEdit ? "Updated successfully" : "Created successfully"),
      });
      setPreviewVisible(false);
      setTimeout(() => navigate("/gst-calc"), 500);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => submitRecord(setSubmitLoading);
  const handleSubmitFromPreview = () => submitRecord(setSubmitFromPreviewLoading);

  // ─── Render: loading ──────────────────────────────────────────────────────

  if (!pageLoad) {
    return (
      <>
        <AppBreadcrumb
          currentMenu="GST Calculation"
          pageName={isEdit ? "Edit GST Calculation" : "Add GST Calculation"}
          pageTitle={isEdit ? "Edit GST Calculation" : "Add GST Calculation"}

        />
        <div className="flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
        </div>
      </>
    );
  }

  // ─── Render: main ─────────────────────────────────────────────────────────

  return (
    <>
      <AppBreadcrumb
        currentMenu="GST Calculation"
        pageName={isEdit ? "Edit GST Calculation" : "Add GST Calculation"}
        pageTitle={isEdit ? "Edit GST Calculation" : "Add GST Calculation"}
        goToPage={true}
      />
      <Toast ref={toast} />

      {/* ── Preview Sidebar ── */}
      <Sidebar
        visible={previewVisible}
        position="right"
        fullScreen
        onHide={() => setPreviewVisible(false)}
        style={{ overflowY: "auto" }}
      >
        {previewData && (
          <div className="p-3">
            {/* Sidebar header */}
            <div className="flex align-items-center gap-2 mb-4 pb-3" style={{ borderBottom: "2px solid #dee2e6" }}>
              <i className="pi pi-calculator" style={{ fontSize: "1.25rem" }} />
              <span style={{ fontWeight: 700, fontSize: "1.1rem" }}>Calculation Preview</span>
            </div>

            {/* Client Info */}
            <Card className="mb-3" style={{ background: "#f8f9fa" }}>
              <div className="grid">
                <div className="col-12 md:col-3">
                  <div style={infoLabelStyle}>Name</div>
                  <div style={infoValueStyle}>{formData.name}</div>
                </div>
                <div className="col-12 md:col-3">
                  <div style={infoLabelStyle}>SIN Number</div>
                  <div style={infoValueStyle}>{formData.sin_number}</div>
                </div>
                {formData.mobile_no && (
                  <div className="col-12 md:col-3">
                    <div style={infoLabelStyle}>Mobile No</div>
                    <div style={infoValueStyle}>{formData.mobile_no}</div>
                  </div>
                )}
                <div className="col-12 md:col-3">
                  <div style={infoLabelStyle}>Year</div>
                  <div style={infoValueStyle}>{formData.year}</div>
                </div>
                <div className="col-12 md:col-3">
                  <div style={infoLabelStyle}>GST %</div>
                  <div style={infoValueStyle}>{formData.gst_percent}%</div>
                </div>
                {formData.gst_no && (
                  <div className="col-12 md:col-3">
                    <div style={infoLabelStyle}>GST No</div>
                    <div style={infoValueStyle}>{formData.gst_no}</div>
                  </div>
                )}
                {formData.access_code && (
                  <div className="col-12 md:col-3">
                    <div style={infoLabelStyle}>Access Code</div>
                    <div style={infoValueStyle}>{formData.access_code}</div>
                  </div>
                )}

              </div>
            </Card>

            {/* Income Table */}
            <Card className="mb-3 h-full section-container">
              <h5 className="mb-2 mt-0" style={{ color: "#2c3e50" }}>Income Breakdown</h5>
              <div className="overflow-x-auto mb-4">
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                  <thead>
                    <tr style={{ background: "#343a40", color: "#fff" }}>
                      <th style={pvThStyle}>Source Type</th>
                      <th style={{ ...pvThStyle, textAlign: "right" }}>Gross</th>
                      <th style={{ ...pvThStyle, textAlign: "right" }}>Tips</th>
                      <th style={{ ...pvThStyle, textAlign: "right" }}>Service Fees</th>
                      <th style={{ ...pvThStyle, textAlign: "center" }}>Has GST</th>
                      <th style={{ ...pvThStyle, textAlign: "right" }}>Net Income</th>
                      <th style={{ ...pvThStyle, textAlign: "right" }}>GST</th>
                      <th style={{ ...pvThStyle, textAlign: "right" }}>Taxable Income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.income_data?.map((row: any, i: number) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fa" }}>
                        <td style={pvTdStyle}>{row.source_type}</td>
                        <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(row.gross)}</td>
                        <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(row.tips)}</td>
                        <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(row.service_fees)}</td>
                        <td style={{ ...pvTdStyle, textAlign: "center" }}>
                          <i className={`pi ${row.has_gst ? "pi-check-circle" : "pi-times-circle"}`}
                            style={{ color: row.has_gst ? "#28a745" : "#dc3545" }} />
                        </td>
                        <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(row.net_after_fees)}</td>
                        <td style={{ ...pvTdStyle, textAlign: "right", color: row.gst > 0 ? "#dc3545" : undefined }}>{fmt(row.gst)}</td>
                        <td style={{ ...pvTdStyle, textAlign: "right", fontWeight: 600 }}>{fmt(row.taxable_income)}</td>
                      </tr>
                    ))}
                    {/* Income Totals Row */}
                    <tr style={{ background: "#e9ecef", fontWeight: 700 }}>
                      <td style={pvTdStyle}>Total</td>
                      <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_gross_income)}</td>
                      <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_tips)}</td>
                      <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_service_fees)}</td>
                      <td style={pvTdStyle}></td>
                      <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_net_after_fees)}</td>
                      <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_gst_collected)}</td>
                      <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_taxable_income)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
            {/* Expense Table */}
            {previewData.expense_data?.length > 0 && (
              <>
                <Card className="mb-3 h-full section-container">
                  <h5 className="mb-2 mt-0 " style={{ color: "#2c3e50" }}>Expense Breakdown</h5>
                  <div className="overflow-x-auto mb-4">
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                      <thead>
                        <tr style={{ background: "#343a40", color: "#fff" }}>
                          <th style={pvThStyle}>Description</th>
                          <th style={{ ...pvThStyle, textAlign: "right" }}>Gross</th>
                          <th style={{ ...pvThStyle, textAlign: "right" }}>GST (ITC)</th>
                          <th style={{ ...pvThStyle, textAlign: "right" }}>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.expense_data.map((row: any, i: number) => (
                          <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8f9fa" }}>
                            <td style={pvTdStyle}>{row.description}</td>
                            <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(row.gross)}</td>
                            <td style={{ ...pvTdStyle, textAlign: "right", color: "#28a745" }}>{fmt(row.gst)}</td>
                            <td style={{ ...pvTdStyle, textAlign: "right", fontWeight: 600 }}>{fmt(row.net)}</td>
                          </tr>
                        ))}
                        <tr style={{ background: "#e9ecef", fontWeight: 700 }}>
                          <td style={pvTdStyle}>Total</td>
                          <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_expense_gross)}</td>
                          <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_expense_gst)}</td>
                          <td style={{ ...pvTdStyle, textAlign: "right" }}>{fmt(previewData.total_expense_net)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="mb-3 flex gap-4">
                    {formData.car_gst != null && (
                      <div><span style={{ fontWeight: 600 }}>Vehicle GST: </span>{fmt(formData.car_gst)}</div>
                    )}
                    {formData.insurance_amount != null && (
                      <div><span style={{ fontWeight: 600 }}>Insurance Amount: </span>{fmt(formData.insurance_amount)}</div>
                    )}
                  </div>
                </Card>
              </>
            )}

            {/* General GST + Simplified GST */}
            <GstSummaryCards data={previewData} cardClassName="mb-3 h-full section-container" totalIncomeLabel="Total Income" />

            {/* Sidebar Submit */}
            <div className="flex justify-content-end gap-2 mt-3 pb-4">
              <Button
                label="Close"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={() => setPreviewVisible(false)}
              />
              <Button
                label={isEdit ? "Update" : "Submit"}
                icon="pi pi-check"
                className="common-primary-button"
                loading={submitFromPreviewLoading}
                onClick={handleSubmitFromPreview}
              />
            </div>

          </div>
        )}
      </Sidebar>

      {/* ── Form ── */}
      <div className="grid" onKeyDown={handleFormKeyNav}>
        <div className="col-12">

          {/* Section A: Basic Info */}
          <Card className="bg-white mb-3">
            <h5 className="mt-0 mb-3">Basic Information</h5>
            <div className="grid p-fluid formgrid">


              <div className="field col-12 md:col-4">
                <label>Name <span className="required">*</span></label>
                <div className="input-effect-div">
                  <InputText
                    value={formData.name}
                    placeholder="Enter Name"
                    onChange={(e) => onFieldChange("name", e.target.value)}
                    className={errors.name ? "p-invalid" : ""}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors.name}</small>
              </div>

              <div className="field col-12 md:col-4">
                <label>SIN Number <span className="required">*</span></label>
                <div className="input-effect-div">
                  <InputText
                    value={formData.sin_number}
                    placeholder="Enter SIN Number"
                    onChange={(e) => onFieldChange("sin_number", e.target.value.replace(/[^\d-]/g, ""))}
                    className={errors.sin_number ? "p-invalid" : ""}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors.sin_number}</small>
              </div>

              <div className="field col-12 md:col-4">
                <label>Mobile No <span className="required">*</span></label>
                <div className="input-effect-div">
                  <InputText
                    value={formData.mobile_no}
                    placeholder="Enter Mobile No"
                    onChange={(e) => onFieldChange("mobile_no", e.target.value.replace(/\D/g, ""))}
                    className={errors.mobile_no ? "p-invalid" : ""}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors.mobile_no}</small>
              </div>

              <div className="field col-12 md:col-4">
                <label>GST No</label>
                <div className="input-effect-div">
                  <InputText
                    value={formData.gst_no}
                    placeholder="Enter 9-digit GST No"
                    maxLength={9}
                    onChange={(e) => onFieldChange("gst_no", e.target.value.replace(/\D/g, ""))}
                    className={errors.gst_no ? "p-invalid" : ""}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors.gst_no}</small>
              </div>

              <div className="field col-12 md:col-4">
                <label>Access Code</label>
                <div className="input-effect-div">
                  <InputText
                    value={formData.access_code}
                    placeholder="Enter 4-digit Access Code"
                    maxLength={4}
                    onChange={(e) => onFieldChange("access_code", e.target.value.replace(/\D/g, ""))}
                    className={errors.access_code ? "p-invalid" : ""}
                  />
                  <span className="focus-bg"></span>
                </div>
                <small className="p-invalid-txt">{errors.access_code}</small>
              </div>



              <div className="field col-12 md:col-4">
                <label>Year <span className="required">*</span></label>
                <Calendar
                  value={formData.year ? new Date(Number(formData.year), 0, 1) : null}
                  view="year"
                  dateFormat="yy"
                  placeholder="Select Year"
                  showIcon
                  onChange={(e) => {
                    const yr = e.value ? new Date(e.value as Date).getFullYear().toString() : "";
                    onFieldChange("year", yr);
                  }}
                  className={errors.year ? "p-invalid" : ""}
                />
                <small className="p-invalid-txt">{errors.year}</small>
              </div>

              {/* GST % */}
              <div className="field col-12 md:col-4">
                <label>GST %</label>
                <InputNumber
                  value={formData.gst_percent}
                  placeholder="e.g. 5"
                  min={0}
                  max={100}
                  mode="decimal"
                  minFractionDigits={0}
                  maxFractionDigits={2}
                  onValueChange={(e) => onFieldChange("gst_percent", e.value)}
                />
              </div>

            </div>
          </Card>

          {/* Section B: Income Data */}
          <Card className="bg-white mb-3">
            <h5 className="mt-0 mb-3">Income Data</h5>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: "collapse", minWidth: "700px" }}>
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Source Type *</th>
                    <th style={thStyle}>Gross ($) *</th>
                    <th style={thStyle}>Tips ($)</th>
                    <th style={thStyle}>Service Fees ($)</th>
                    <th style={thStyle}>Has GST</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Net ($)</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>GST ($)</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {incomeRows.map((row, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={tdStyle}>
                        <Dropdown
                          value={row.source_type}
                          options={[...incomeTypeOptions, { label: "__add__", value: "__ADD_NEW__" }]}
                          placeholder="Select type"
                          onChange={(e) => {
                            if (e.value === "__ADD_NEW__") { setIncomeDialogVisible(true); return; }
                            onIncomeChange(i, "source_type", e.value);
                          }}
                          itemTemplate={(option) => {
                            if (option.value === "__ADD_NEW__") {
                              return (
                                <div className="flex align-items-center gap-2" style={{ color: "#2196F3", fontWeight: 600, borderTop: "1px solid #dee2e6", margin: "4px -14px -6px", padding: "8px 14px" }}>
                                  <i className="pi pi-plus-circle" /><span>Add New</span>
                                </div>
                              );
                            }
                            return <span>{option.label}</span>;
                          }}
                          className={errors[`income_${i}_source_type`] ? "p-invalid w-full" : "w-full"}
                          style={{ minWidth: "190px" }}
                        />
                        <small className="p-invalid-txt">{errors[`income_${i}_source_type`]}</small>
                      </td>
                      <td style={tdStyle}>
                        <InputNumber
                          value={row.gross}
                          min={0}
                          mode="decimal"
                          minFractionDigits={2}
                          maxFractionDigits={2}
                          placeholder="0.00"
                          onValueChange={(e) => onIncomeChange(i, "gross", e.value ?? null)}
                          inputClassName={errors[`income_${i}_gross`] ? "p-invalid" : ""}
                          style={{ width: "130px" }}
                        />
                        <small className="p-invalid-txt">{errors[`income_${i}_gross`]}</small>
                      </td>
                      <td style={tdStyle}>
                        <InputNumber
                          value={row.tips}
                          min={0}
                          mode="decimal"
                          minFractionDigits={2}
                          maxFractionDigits={2}
                          placeholder="0.00"
                          onValueChange={(e) => onIncomeChange(i, "tips", e.value ?? null)}
                          style={{ width: "130px" }}
                        />
                      </td>
                      <td style={tdStyle}>
                        <InputNumber
                          value={row.service_fees}
                          min={0}
                          mode="decimal"
                          minFractionDigits={2}
                          maxFractionDigits={2}
                          placeholder="0.00"
                          onValueChange={(e) => onIncomeChange(i, "service_fees", e.value ?? null)}
                          style={{ width: "130px" }}
                        />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <Checkbox
                          checked={!!row.has_gst}
                          onChange={(e) => onIncomeChange(i, "has_gst", e.checked)}
                        />
                      </td>
                      {(() => {
                        const c = calcIncomeRow(row, formData.gst_percent ?? 5); return (
                          <>
                            <td style={{ ...tdStyle, textAlign: "right", color: "#2c3e50", fontWeight: 500 }}>{fmt(c.net_after_fees)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", color: row.has_gst ? "#dc3545" : "#6c757d", fontWeight: 500 }}>{fmt(c.gst)}</td>
                          </>
                        );
                      })()}
                      <td style={tdStyle}>
                        {incomeRows.length > 1 && (
                          <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-sm p-button-text"
                            tooltip="Remove row"
                            tooltipOptions={{ position: "bottom" }}
                            onClick={() => removeIncomeRow(i)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3">
              <Button
                icon="pi pi-plus"
                label="Add Income Row"
                className="p-button-outlined p-button-sm common-p-outline-button"
                onClick={addIncomeRow}
              />
            </div>
          </Card>

          {/* Section C: Expense Data */}
          <Card className="bg-white mb-3">
            <h5 className="mt-0 mb-3">Expense Data</h5>
            <div className="overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: "collapse", minWidth: "400px" }}>
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Description *</th>
                    <th style={thStyle}>Gross ($) *</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>GST ($)</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Net ($)</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {expenseRows.map((row, i) => (
                    <tr key={i}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={tdStyle}>
                        <Dropdown
                          value={row.description}
                          options={[...expenseDescOptions, { label: "__add__", value: "__ADD_NEW__" }]}
                          placeholder="Select description"

                          onChange={(e) => {
                            if (e.value === "__ADD_NEW__") { setExpenseDialogVisible(true); return; }
                            onExpenseChange(i, "description", e.value);
                          }}
                          itemTemplate={(option) => {
                            if (option.value === "__ADD_NEW__") {
                              return (
                                <div className="flex align-items-center gap-2" style={{ color: "#2196F3", fontWeight: 600, borderTop: "1px solid #dee2e6", margin: "4px -14px -6px", padding: "8px 14px" }}>
                                  <i className="pi pi-plus-circle" /><span>Add New</span>
                                </div>
                              );
                            }
                            return <span>{option.label}</span>;
                          }}
                          className={errors[`expense_${i}_description`] ? "p-invalid w-full" : "w-full"}
                          style={{ minWidth: "180px" }}
                        />
                        <small className="p-invalid-txt">{errors[`expense_${i}_description`]}</small>
                      </td>
                      <td style={tdStyle}>
                        <InputNumber
                          value={row.gross}
                          min={0}
                          mode="decimal"
                          minFractionDigits={2}
                          maxFractionDigits={2}
                          placeholder="0.00"
                          onValueChange={(e) => onExpenseChange(i, "gross", e.value ?? null)}
                          inputClassName={errors[`expense_${i}_gross`] ? "p-invalid" : ""}
                          style={{ width: "150px" }}
                        />
                        <small className="p-invalid-txt">{errors[`expense_${i}_gross`]}</small>
                      </td>
                      {(() => {
                        const c = calcExpenseRow(row, formData.gst_percent ?? 5); return (
                          <>
                            <td style={{ ...tdStyle, textAlign: "right", color: "#dc3545", fontWeight: 500 }}>{fmt(c.gst)}</td>
                            <td style={{ ...tdStyle, textAlign: "right", color: "#2c3e50", fontWeight: 500 }}>{fmt(c.net)}</td>
                          </>
                        );
                      })()}
                      <td style={tdStyle}>
                        {expenseRows.length > 1 && (
                          <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-sm p-button-text"
                            tooltip="Remove row"
                            tooltipOptions={{ position: "bottom" }}
                            onClick={() => removeExpenseRow(i)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ background: "#e9ecef", fontWeight: 700 }}>
                    <td colSpan={3} style={thStyle}>Total</td>
                    <td style={{ ...thStyle, textAlign: "right", color: "#dc3545" }}>{fmt(liveCalc.total_expense_gst)}</td>
                    <td style={{ ...thStyle, textAlign: "right" }}>{fmt(liveCalc.total_expense_net)}</td>
                    <td style={thStyle}></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-3">
              <Button
                icon="pi pi-plus"
                label="Add Expense Row"
                className="p-button-outlined p-button-sm common-p-outline-button"
                onClick={addExpenseRow}
              />
            </div>
            <div className="mt-3 flex align-items-center gap-6 flex-wrap">
              <div className="flex align-items-center gap-2">
                <label style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Vehicle GST ($)</label>
                <InputNumber
                  value={formData.car_gst}
                  placeholder="0.00"
                  min={0}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  onValueChange={(e) => onFieldChange("car_gst", e.value)}
                  style={{ width: "160px" }}
                />
              </div>
              <div className="flex align-items-center gap-2">
                <label style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Insurance Amount ($)</label>
                <InputNumber
                  value={formData.insurance_amount}
                  placeholder="0.00"
                  min={0}
                  mode="decimal"
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  onValueChange={(e) => onFieldChange("insurance_amount", e.value ?? null)}
                  style={{ width: "160px" }}
                />
              </div>
            </div>
          </Card>

          {/* Section D: Live GST Summary */}
          <div className="mb-3">
            <GstSummaryCards data={liveCalc} cardClassName="bg-white h-full" totalIncomeLabel="Total Net Income" />
          </div>

          {/* Footer Actions */}
          <div className="flex justify-content-end gap-2 mt-2 mb-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => navigate("/gst-calc")}
            />
            <Button
              label="Preview"
              icon="pi pi-eye"
              className="p-button-outlined common-p-outline-button"
              loading={previewLoading}
              onClick={handlePreview}
            />
            <Button
              label={isEdit ? "Update" : "Submit"}
              icon="pi pi-check"
              className="common-primary-button"
              loading={submitLoading}
              onClick={handleSubmit}
            />
          </div>

        </div>
      </div>

      {/* ── Add Income Type Dialog ── */}
      <Dialog
        header="Add Income Type"
        visible={incomeDialogVisible}
        style={{ width: "420px" }}
        onHide={() => { setIncomeDialogVisible(false); setNewIncomeForm({ name: "", has_gst: false, is_uber_main: false }); setIncomeNameError(""); }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => { setIncomeDialogVisible(false); setNewIncomeForm({ name: "", has_gst: false, is_uber_main: false }); setIncomeNameError(""); }} />
            <Button label="Add" icon="pi pi-check" className="common-primary-button" loading={incomeAddLoading} onClick={handleAddIncomeType} />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="field mb-3">
            <label className="font-semibold">Name <span className="required">*</span></label>
            <InputText
              value={newIncomeForm.name}
              placeholder="Enter income type name"
              className={incomeNameError ? "p-invalid" : ""}
              onChange={(e) => { setNewIncomeForm((prev) => ({ ...prev, name: e.target.value })); setIncomeNameError(""); }}
            />
            <small className="p-invalid-txt">{incomeNameError}</small>
          </div>
          <div className="field mb-3 flex align-items-center gap-3">
            <Checkbox
              inputId="dlg_has_gst"
              checked={newIncomeForm.has_gst}
              onChange={(e) => setNewIncomeForm((prev) => ({ ...prev, has_gst: !!e.checked }))}
            />
            <label htmlFor="dlg_has_gst" className="mb-0">Has GST</label>
          </div>
          <div className="field flex align-items-center gap-3">
            <Checkbox
              inputId="dlg_is_uber_main"
              checked={newIncomeForm.is_uber_main}
              onChange={(e) => setNewIncomeForm((prev) => ({ ...prev, is_uber_main: !!e.checked }))}
            />
            <label htmlFor="dlg_is_uber_main" className="mb-0">Exclude Tips in Net Income <br /> (If you check this then tips won't be added in Net Income)</label>
          </div>
        </div>
      </Dialog>

      {/* ── Add Expense Type Dialog ── */}
      <Dialog
        header="Add Expense Type"
        visible={expenseDialogVisible}
        style={{ width: "380px" }}
        onHide={() => { setExpenseDialogVisible(false); setNewExpenseName(""); setExpenseNameError(""); }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={() => { setExpenseDialogVisible(false); setNewExpenseName(""); setExpenseNameError(""); }} />
            <Button label="Add" icon="pi pi-check" className="common-primary-button" loading={expenseAddLoading} onClick={handleAddExpenseDesc} />
          </div>
        }
      >
        <div className="p-fluid">
          <div className="field">
            <label className="font-semibold">Name <span className="required">*</span></label>
            <InputText
              value={newExpenseName}
              placeholder="Enter expense type name"
              className={expenseNameError ? "p-invalid" : ""}
              onChange={(e) => { setNewExpenseName(e.target.value); setExpenseNameError(""); }}
            />
            <small className="p-invalid-txt">{expenseNameError}</small>
          </div>
        </div>
      </Dialog>

    </>
  );
};


// ─── Styles ───────────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderBottom: "2px solid #dee2e6",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "8px 12px",
  verticalAlign: "top",
  borderBottom: "1px solid #dee2e6",
};

const pvThStyle: React.CSSProperties = {
  padding: "10px 14px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: "0.8rem",
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
};

const pvTdStyle: React.CSSProperties = {
  padding: "9px 14px",
  borderBottom: "1px solid #dee2e6",
  fontSize: "0.875rem",
};

const infoLabelStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "#6c757d",
  marginBottom: "2px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const infoValueStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: "0.95rem",
  color: "#2c3e50",
};

export default AddEditUberCalc;
